import { Box, Button, Grid as MuiGrid, Tooltip, Typography } from '@mui/material';

export default function (props: { 
    config: any, 
    setActiveTab: (index: number) => void, 
    activeTab: number,
    isReadyToProcess: () => boolean | ""
}) {
    const { config, setActiveTab, activeTab, isReadyToProcess } = props;
    
    // Handle both function and boolean value for isReadyToProcess
    const getIsReadyToProcess = () => {
        if (typeof isReadyToProcess === 'function') {
            return isReadyToProcess();
        }
        return isReadyToProcess;
    };

    return (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3, pb: 2 }}>
            <MuiGrid container spacing={1}>
              {[
                { 
                  label: 'Basic Info', 
                  complete: config.name && config.config_id, 
                  required: true,
                  tooltip: "Set the basic details for this batch keyword classification configuration."
                },
                { 
                  label: 'Topics', 
                  complete: Object.keys(config.topic_mapping).length > 0, 
                  required: true,
                  tooltip: "Define how keywords should be mapped to topics in your classification system."
                },
                { 
                  label: 'Prompt', 
                  complete: config.prompt.system_prompt, 
                  required: true,
                  tooltip: "Configure the prompt that will be sent to the AI model for classification."
                },
                { 
                  label: 'Results', 
                  complete: config.results.output_format, 
                  required: true,
                  tooltip: "Define how the AI responses should be processed and formatted."
                },
                { 
                  label: 'Process', 
                  complete: getIsReadyToProcess(), 
                  required: false,
                  tooltip: "Run the batch classification process with your configured settings."
                }
              ].map((step, index) => (
                <MuiGrid item key={index}>
                  <Tooltip title={step.tooltip} arrow placement="top">
                    <span> {/* Wrapper to ensure tooltip works when button is disabled */}
                      <Button
                        onClick={() => setActiveTab(index)}
                        sx={{ 
                          minWidth: '120px',
                          borderRadius: '8px',
                          px: 2,
                          py: 1.2,
                          backgroundColor: activeTab === index ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                          border: '1px solid',
                          borderColor: activeTab === index ? 'primary.main' : 
                                      step.complete ? 'rgba(0, 0, 0, 0.12)' : 'rgba(0, 0, 0, 0.08)',
                          color: 'text.primary',
                          boxShadow: activeTab === index ? '0 2px 4px rgba(0,0,0,0.05)' : 'none',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            backgroundColor: activeTab === index 
                              ? 'rgba(25, 118, 210, 0.12)' 
                              : 'rgba(0, 0, 0, 0.04)',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          }
                        }}
                        disabled={
                          // Disable steps that require previous steps to be completed first
                          (index > 0 && !config.name) || 
                          (index > 1 && Object.keys(config.topic_mapping).length === 0) ||
                          (index > 2 && !config.prompt.system_prompt) ||
                          (index > 3 && !config.results.output_format)
                        }
                      >
                        <Box display="flex" alignItems="center">
                          <Box sx={{ 
                            width: 28, 
                            height: 28, 
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: activeTab === index ? 'primary.main' : 
                                            step.complete ? 'rgba(25, 118, 210, 0.12)' : 'rgba(0, 0, 0, 0.04)',
                            color: activeTab === index ? 'white' : 
                                  step.complete ? 'primary.main' : 'text.secondary',
                            mr: 1.5,
                            fontSize: '0.875rem',
                            fontWeight: 'medium',
                            transition: 'all 0.2s ease',
                          }}>
                            {step.complete ? '✓' : index + 1}
                          </Box>
                          <Box>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontWeight: activeTab === index ? 'medium' : 'regular',
                                color: activeTab === index ? 'primary.main' : 'text.primary'
                              }}
                            >
                              {step.label}
                            </Typography>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                display: 'block',
                                color: step.complete ? 'primary.main' : 'text.secondary',
                                opacity: 0.8
                              }}
                            >
                              {index === 0 && (config.name ? 'Complete' : 'Required')}
                              {index === 1 && (
                                Object.keys(config.topic_mapping).length > 0 
                                  ? `${Object.keys(config.topic_mapping).length} mappings` 
                                  : 'No mappings'
                              )}
                              {index === 2 && (config.prompt.system_prompt ? 'Complete' : 'Required')}
                              {index === 3 && (config.results.output_format ? 'Complete' : 'Required')}
                              {index === 4 && (getIsReadyToProcess() ? 'Ready to run' : 'Complete previous steps')}
                            </Typography>
                          </Box>
                        </Box>
                      </Button>
                    </span>
                  </Tooltip>
                </MuiGrid>
              ))}
            </MuiGrid>
        </Box>
    )
}